import { useCallback, useState } from "react";

import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";

export type TrackingModalType =
  | "allocation"
  | "gpEstimatedMaxTicketSize"
  | "gpEstimatedMinTicketSize"
  | "gpIndicativeOrder"
  | "ioi"
  | "lpMaxTicketSize"
  | "lpMinTicketSize";

type ModalState = {
  activeModal: TrackingModalType | null;
  selectedItem: DealTrackingLimitedPartnerBreakdown | null;
};

type OpenModalFunction = (item: DealTrackingLimitedPartnerBreakdown) => void;

type UseTrackingModalsReturn = {
  activeModal: TrackingModalType | null;
  selectedItem: DealTrackingLimitedPartnerBreakdown | null;
  openAllocationModal: OpenModalFunction;
  openIoiModal: OpenModalFunction;
  openLpMinTicketSizeModal: OpenModalFunction;
  openLpMaxTicketSizeModal: OpenModalFunction;
  openGpEstimatedMinTicketSizeModal: OpenModalFunction;
  openGpEstimatedMaxTicketSizeModal: OpenModalFunction;
  openGpIndicativeOrderModal: OpenModalFunction;
  closeModal: () => void;
  isModalOpen: (modal: TrackingModalType) => boolean;
};

export const canOpenModalForItem = (
  canEdit: boolean,
  item: DealTrackingLimitedPartnerBreakdown,
): boolean => canEdit && !item.isClosed;

export const useTrackingModals = (
  canEditDealOpportunity: boolean,
): UseTrackingModalsReturn => {
  const [state, setState] = useState<ModalState>({
    activeModal: null,
    selectedItem: null,
  });

  const closeModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      activeModal: null,
      selectedItem: null,
    }));
  }, []);

  const openModal = useCallback(
    (modal: TrackingModalType) =>
      (item: DealTrackingLimitedPartnerBreakdown) => {
        if (!canOpenModalForItem(canEditDealOpportunity, item)) return;
        setState((prev) => ({
          ...prev,
          activeModal: modal,
          selectedItem: item,
        }));
      },
    [canEditDealOpportunity],
  );

  const openAllocationModal = openModal("allocation");
  const openIoiModal = openModal("ioi");
  const openLpMinTicketSizeModal = openModal("lpMinTicketSize");
  const openLpMaxTicketSizeModal = openModal("lpMaxTicketSize");
  const openGpEstimatedMinTicketSizeModal = openModal(
    "gpEstimatedMinTicketSize",
  );
  const openGpEstimatedMaxTicketSizeModal = openModal(
    "gpEstimatedMaxTicketSize",
  );
  const openGpIndicativeOrderModal = openModal("gpIndicativeOrder");

  const isModalOpen = useCallback(
    (modal: TrackingModalType) => state.activeModal === modal,
    [state.activeModal],
  );

  return {
    activeModal: state.activeModal,
    selectedItem: state.selectedItem,
    openAllocationModal,
    openIoiModal,
    openLpMinTicketSizeModal,
    openLpMaxTicketSizeModal,
    openGpEstimatedMinTicketSizeModal,
    openGpEstimatedMaxTicketSizeModal,
    openGpIndicativeOrderModal,
    closeModal,
    isModalOpen,
  };
};
